import { DatasetStatus, PendingStatus } from '@components'
import { Submission } from '@global/types'
import { Phase } from './config'

interface getActiveSubmissionStepProps {
  progressMap: Record<
    Phase,
    {
      step: number
      progress: number
    }
  >
  phase: string
  creativeClassificationEnabled: boolean
}

/**
 *
 * @param progressMap
 * @param phase
 * @param creativeClassificationEnabled
 * @returns
 */
export function getActiveSubmissionStep({
  progressMap,
  phase,
  creativeClassificationEnabled,
}: getActiveSubmissionStepProps) {
  // If creative classification is enabled and the phase is completed (which is step 5), it means that
  // we have an additional step, so we need to add 1 to the active step
  if (creativeClassificationEnabled && phase === Phase.COMPLETED) {
    return (progressMap[phase]?.step || 5) + 1
  }

  return progressMap[phase]?.step
}

interface ApprovalFlowDetails {
  approvalLabel:
    | typeof DatasetStatus.APPROVED
    | typeof PendingStatus.AWAITING_APPROVAL
    | typeof PendingStatus.REJECTED
    | null
  approvalActionTimestamp?: string | null
  validatingPhaseLabelForApprovalStep?: DatasetStatus | PendingStatus | null
}

/**
 * Creates a consolidated object containing the approval data needed to correctly
 * display the SubmissionStatusPopover
 * @param {boolean} approvalFlowEnabled
 * @param {Submission} submission
 */
export function useApprovalFlowDetails(
  approvalFlowEnabled: boolean,
  submission: Submission,
): ApprovalFlowDetails {
  if (!approvalFlowEnabled) {
    return {
      approvalLabel: null,
      approvalActionTimestamp: null,
      validatingPhaseLabelForApprovalStep: null,
    }
  }

  const approvalResult = submission?.approval_result
  const approvalActionTimestamp = approvalResult?.created_at
  const approvalLabel = getApprovalLabel(approvalResult?.approval_status)
  const validatingPhaseLabelForApprovalStep = getValidatingPhaseLabelForApprovalStep(
    approvalResult?.approval_status || submission.status,
    approvalFlowEnabled,
  )

  return {
    approvalLabel,
    approvalActionTimestamp,
    validatingPhaseLabelForApprovalStep,
  }
}

function getApprovalLabel(approvalStatus) {
  if (approvalStatus === DatasetStatus.APPROVED) {
    return DatasetStatus.APPROVED
  }

  if (approvalStatus === PendingStatus.REJECTED) {
    return PendingStatus.REJECTED
  }

  return PendingStatus.AWAITING_APPROVAL
}

function getValidatingPhaseLabelForApprovalStep(approvalStatus, approvalFlowEnabled) {
  if (approvalStatus === PendingStatus.REJECTED) {
    return PendingStatus.REJECTED
  }

  if (approvalStatus === PendingStatus.AWAITING_APPROVAL) {
    return PendingStatus.AWAITING_APPROVAL
  }

  return approvalFlowEnabled ? PendingStatus.AWAITING_APPROVAL : null
}
